import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PublicOnlyRoute } from '@/components/auth/PublicOnlyRoute'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { ResidentLayout } from '@/components/layout/ResidentLayout'
import { PlatformIndicator } from '@/components/PlatformIndicator'
import { NewMessageToastListener } from '@/components/notifications/NewMessageToastListener'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage'
import { TermsOfUsePage } from '@/pages/TermsOfUsePage'
import { HomePage } from '@/pages/HomePage'
import { AnnouncementDetailPage } from '@/pages/AnnouncementDetailPage'
import { ProfileLayout } from '@/pages/ProfileLayout'
import { PersonalPage } from '@/pages/profile/PersonalPage'
import { AddressPage } from '@/pages/profile/AddressPage'
import { TagsPage } from '@/pages/profile/TagsPage'
import { FamilyPage } from '@/pages/profile/FamilyPage'
import { EducationPage } from '@/pages/profile/EducationPage'
import { EmploymentPage } from '@/pages/profile/EmploymentPage'
import { OrganizationsPage } from '@/pages/profile/OrganizationsPage'
import { DocumentsPage } from '@/pages/profile/DocumentsPage'
import { ChatListPage } from '@/pages/ChatListPage'
import { NewChatPage } from '@/pages/NewChatPage'
import { ConversationPage } from '@/pages/ConversationPage'
import { BimssPage } from '@/pages/BimssPage'
import { BimssComingSoonPage } from '@/pages/BimssComingSoonPage'
import { ComingSoonPage } from '@/pages/ComingSoonPage'
import { LandingPage } from '@/pages/LandingPage'
import { NotFoundPage } from '@/pages/NotFoundPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NewMessageToastListener />
        <Routes>
          <Route element={<PublicOnlyRoute />}>
            <Route element={<PublicLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Route>
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route element={<ResidentLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/announcement/:id" element={<AnnouncementDetailPage />} />
              <Route path="/profile" element={<ProfileLayout />}>
                <Route index element={<Navigate to="/profile/personal" replace />} />
                <Route path="personal" element={<PersonalPage />} />
                <Route path="address" element={<AddressPage />} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="family" element={<FamilyPage />} />
                <Route path="education" element={<EducationPage />} />
                <Route path="employment" element={<EmploymentPage />} />
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="documents" element={<DocumentsPage />} />
              </Route>
              <Route path="/chat" element={<ChatListPage />} />
              <Route path="/chat/new" element={<NewChatPage />} />
              <Route path="/chat/:id" element={<ConversationPage />} />
              <Route path="/bimss" element={<BimssPage />} />
              <Route path="/bimss/coming-soon" element={<BimssComingSoonPage />} />
              <Route path="/services" element={<ComingSoonPage title="Services" />} />
              <Route path="/sos" element={<ComingSoonPage title="SOS" />} />
            </Route>
          </Route>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <PlatformIndicator />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
